import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import { updateTiunSnippet } from '@components/Tiun/utils'

export type GetCurrentTiunSnippetState = (queryClient: QueryClient) => boolean
type GetUserConnectedWithTiunState = (queryClient: QueryClient) => boolean

const getCurrentTiunSnippetState: GetCurrentTiunSnippetState = (queryClient) =>
  !!queryClient.getQueryData<boolean>(['show-tiun-snippet'])

const getUserConnectedWithTiunState: GetUserConnectedWithTiunState = (
  queryClient,
) => !!queryClient.getQueryData<boolean>(['is-user-connected-with-tiun'])

const useShouldShowTiunSnippet = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const [tiunSnippetState, setTiunSnippetState] = useState<boolean>(() =>
    getUserConnectedWithTiunState(queryClient),
  )

  const resetTiunSnippetState = useCallback(() => {
    updateTiunSnippet(queryClient, getUserConnectedWithTiunState(queryClient))
  }, [queryClient])

  const unsubscribeFnRef = useRef<() => void>(undefined)

  const updateTiunSnippetState = useCallback(
    (args: any) => {
      if (
        args?.action?.type === 'invalidate' &&
        args?.query?.queryKey[0] === 'show-tiun-snippet'
      ) {
        setTiunSnippetState(getCurrentTiunSnippetState(queryClient))
      }

      if (
        args?.action?.type === 'invalidate' &&
        args?.query?.queryKey[0] === 'is-user-connected-with-tiun'
      ) {
        updateTiunSnippet(
          queryClient,
          getUserConnectedWithTiunState(queryClient),
        )
      }
    },
    [queryClient],
  )

  useExecuteOnClientNavigation(resetTiunSnippetState)

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateTiunSnippetState)
    setTiunSnippetState(getCurrentTiunSnippetState(queryClient))

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateTiunSnippetState, queryCache, queryClient])

  return tiunSnippetState
}

export default useShouldShowTiunSnippet
